import { onDomChanges } from "../../components/dynamic/observer";
import { singleInit } from "../../components/dynamic/bind";

function formatPrice(number) {
  return number.toLocaleString('ru-RU') + ' ₽';
}

function initDeliveryVariants(cartBlock) {
  const variantsRaw = cartBlock.dataset.variantsData;
  if (!variantsRaw) return;

  let variants;
  try {
    variants = JSON.parse(variantsRaw);
  } catch (e) {
    return;
  }

  if (!variants || !variants.items || variants.items.length === 0) return;

  const isCarTyre = cartBlock.dataset.isCarTyre === '1';
  const quantityInput = cartBlock.querySelector('[data-cart-quantity]');
  const increaseBtn = cartBlock.querySelector('[data-cart-quantity-increase]');
  const decreaseBtn = cartBlock.querySelector('[data-cart-quantity-decrease]');

  const singlePriceEl = cartBlock.querySelector('[data-single-price]');
  const kitPriceEl = cartBlock.querySelector('[data-kit-price]');
  const totalPriceEl = cartBlock.querySelector('[data-total-price]');
  const totalPriceCountLabel = cartBlock.querySelector('[data-total-price-count-text]');
  const pickupEl = cartBlock.querySelector('[data-delivery-pickup]');

  const variantsBlock = cartBlock.querySelector('[data-variants-block]');
  const cardFastestTerm = cartBlock.querySelector('[data-card-fastest-term]');
  const cardFastestPrice = cartBlock.querySelector('[data-card-fastest-price]');
  const cardCheapestTerm = cartBlock.querySelector('[data-card-cheapest-term]');
  const cardCheapestPrice = cartBlock.querySelector('[data-card-cheapest-price]');
  const cardCheapestOldPrice = cartBlock.querySelector('[data-card-cheapest-old-price]');

  const addToCartBtn = cartBlock.querySelector('[data-cart-add]');
  const fastBuyBtn = cartBlock.querySelector('[data-fast-buy-btn]');

  const variantCards = cartBlock.querySelectorAll('[data-variant-card]');

  const groupOffersBlock = document.querySelector('.product-page-group-offers');
  const tableRows = groupOffersBlock ? groupOffersBlock.querySelectorAll('[data-table-offer-id]') : [];

  let currentOption = 'fastest';

  function getCount() {
    if (!quantityInput) return 1;
    const val = parseInt(quantityInput.value, 10);
    return isNaN(val) || val < 1 ? 1 : val;
  }

  function getOfferSplit(count, strategy) {
    let sorted;
    if (strategy === 'fastest') {
      // Fastest first: lowest position first. If positions equal, lowest price first.
      sorted = [...variants.items].sort((a, b) => {
        const posA = a.position ?? 999;
        const posB = b.position ?? 999;
        if (posA !== posB) return posA - posB;
        return a.price - b.price;
      });
    } else {
      // Cheapest first: lowest price first. If prices equal, lowest position (faster) first.
      sorted = [...variants.items].sort((a, b) => {
        if (a.price !== b.price) return a.price - b.price;
        return (a.position ?? 999) - (b.position ?? 999);
      });
    }

    let needed = count;
    const split = [];

    for (const item of sorted) {
      if (needed <= 0) break;
      const available = item.quantity;
      if (available > 0 || item.isPreorder) {
        const take = item.isPreorder ? needed : Math.min(needed, available);
        if (take > 0) {
          split.push({
            id: item.id,
            item: item,
            quantity: take,
            price: item.price
          });
          needed -= take;
        }
      }
    }

    // Fallback if needed > available total
    if (needed > 0) {
      if (split.length > 0) {
        split[split.length - 1].quantity += needed;
      } else if (sorted.length > 0) {
        split.push({
          id: sorted[0].id,
          item: sorted[0],
          quantity: needed,
          price: sorted[0].price
        });
      }
    }

    // Calculate total price and slowest item among used offers
    let totalPrice = 0;
    let slowestItem = split[0]?.item || variants.fastest || variants.items[0];
    let maxPosition = -1;

    for (const s of split) {
      totalPrice += s.quantity * s.price;
      const pos = s.item.position ?? 0;
      if (pos > maxPosition) {
        maxPosition = pos;
        slowestItem = s.item;
      }
    }

    return {
      items: split,
      totalPrice: totalPrice,
      slowestItem: slowestItem,
      slowestPosition: maxPosition
    };
  }

  function update() {
    const count = getCount();
    const splitFastest = getOfferSplit(count, 'fastest');
    const splitCheapest = getOfferSplit(count, 'cheapest');

    // Choice exists if cheapest is strictly cheaper than fastest
    const hasChoice = splitCheapest.totalPrice < splitFastest.totalPrice;

    if (variantsBlock) {
      variantsBlock.style.display = hasChoice ? '' : 'none';
    }

    // If no choice, currentOption always reverts to fastest
    if (!hasChoice) {
      currentOption = 'fastest';
    }

    // 1. Update variant cards if present
    if (cardFastestTerm) {
      cardFastestTerm.textContent = splitFastest.slowestItem.label;
    }
    if (cardFastestPrice) {
      cardFastestPrice.textContent = formatPrice(splitFastest.totalPrice);
    }
    if (cardCheapestTerm) {
      cardCheapestTerm.textContent = splitCheapest.slowestItem.label;
    }
    if (cardCheapestPrice) {
      cardCheapestPrice.textContent = formatPrice(splitCheapest.totalPrice);
    }
    if (cardCheapestOldPrice) {
      cardCheapestOldPrice.textContent = formatPrice(splitFastest.totalPrice);
    }

    // Update active class and radio buttons
    variantCards.forEach((card) => {
      const option = card.dataset.variantCard;
      const radio = card.querySelector('input[type="radio"]');
      if (option === currentOption) {
        card.classList.add('_active');
        if (radio) radio.checked = true;
      } else {
        card.classList.remove('_active');
        if (radio) radio.checked = false;
      }
    });

    // 2. Determine active split
    const activeSplit = (currentOption === 'cheapest' && hasChoice) ? splitCheapest : splitFastest;
    const activeOffer = activeSplit.slowestItem;

    // 3. Update main single price & kit price
    const unitPrice = Math.round(activeSplit.totalPrice / count);
    if (singlePriceEl) {
      singlePriceEl.textContent = formatPrice(unitPrice);
    }
    if (kitPriceEl && isCarTyre) {
      kitPriceEl.textContent = formatPrice(unitPrice * 4) + ' комплект';
    }

    // 4. Update total price next to quantity
    if (totalPriceEl) {
      totalPriceEl.textContent = formatPrice(activeSplit.totalPrice);
    }
    if (totalPriceCountLabel) {
      totalPriceCountLabel.textContent = ` за ${count} шт`;
    }

    // 5. Update pickup string
    if (pickupEl) {
      pickupEl.textContent = activeSplit.slowestItem.pickup;
    }

    // 6. Update action buttons
    if (addToCartBtn) {
      addToCartBtn.dataset.id = activeOffer.id;
      addToCartBtn.dataset.cartQuantity = count;
      addToCartBtn.dataset.cartItems = JSON.stringify(activeSplit.items.map(s => ({ id: s.id, quantity: s.quantity })));
    }
    if (fastBuyBtn) {
      fastBuyBtn.dataset.id = activeOffer.id;
      const basePath = fastBuyBtn.dataset.fastBuyBasePath;
      const buyable = fastBuyBtn.dataset.buyable;
      if (basePath && buyable) {
        fastBuyBtn.setAttribute('href', `${basePath}?buyable=${encodeURIComponent(buyable)}&id=${activeOffer.id}`);
      }
    }

    // 7. Update table "Варианты доставки"
    const baseFastestOffer = variants.fastest || variants.items[0];
    tableRows.forEach((row) => {
      const rowUnitPrice = parseInt(row.dataset.tableUnitPrice, 10);
      const priceEl = row.querySelector('[data-table-price]');
      const oldPriceEl = row.querySelector('[data-table-old-price]');
      const economyEl = row.querySelector('[data-table-economy]');

      if (priceEl && !isNaN(rowUnitPrice)) {
        priceEl.textContent = formatPrice(count * rowUnitPrice);
      }

      if (!isNaN(rowUnitPrice) && baseFastestOffer) {
        const unitDiff = baseFastestOffer.price - rowUnitPrice;
        if (unitDiff > 0) {
          if (priceEl) priceEl.classList.add('_economy');
          if (oldPriceEl) {
            oldPriceEl.style.display = '';
            oldPriceEl.textContent = formatPrice(count * baseFastestOffer.price);
          }
          if (economyEl) {
            economyEl.style.display = '';
            const badgeDesktop = economyEl.querySelector('[data-table-economy-desktop]');
            const badgeMobile = economyEl.querySelector('[data-table-economy-mobile]');
            if (badgeDesktop) {
              badgeDesktop.textContent = `Экономия ${formatPrice(count * unitDiff)}`;
            }
            if (badgeMobile) {
              badgeMobile.textContent = `- ${formatPrice(count * unitDiff)}`;
            }
            if (!badgeDesktop && !badgeMobile) {
              const badgeEl = economyEl.querySelector('.product-page-group-offers__economy-badge') || economyEl;
              badgeEl.textContent = `Экономия ${formatPrice(count * unitDiff)}`;
            }
          }
        } else {
          if (priceEl) priceEl.classList.remove('_economy');
          if (oldPriceEl) {
            oldPriceEl.style.display = 'none';
          }
          if (economyEl) {
            economyEl.style.display = 'none';
          }
        }
      }
    });
  }

  // Handle radio / card selection
  variantCards.forEach((card) => {
    card.addEventListener('click', (e) => {
      const option = card.dataset.variantCard;
      if (!option || option === currentOption) return;
      currentOption = option;
      update();
    });
  });

  // Listen to quantity changes
  if (quantityInput) {
    quantityInput.addEventListener('input', update);
    quantityInput.addEventListener('change', update);
  }
  if (increaseBtn) {
    increaseBtn.addEventListener('click', update);
  }
  if (decreaseBtn) {
    decreaseBtn.addEventListener('click', update);
  }

  // Initial render
  update();
}

onDomChanges(() => {
  document.querySelectorAll('[data-cart-block]').forEach((cartBlock) => {
    singleInit(cartBlock, 'deliveryVariantsInitialized', initDeliveryVariants);
  });
});
